import type {
  AttentionMetric,
  OrderFlowStep,
  OrderStatistics,
} from '@/types/dashboard';
import { routes } from '@/routes/paths';

/**
 * Real Ezzo order lifecycle as exposed by DashboardController::order_stats_calc.
 * Transitions mirror OrderDetailDrawer actions — not invented stages.
 */
export const ORDER_FLOW_STEPS: OrderFlowStep[] = [
  {
    key: 'searching_for_dm',
    label: 'Unassigned',
    shortLabel: 'Unassigned',
    description: 'Waiting for a deliveryman',
    statusQuery: 'searching_for_deliverymen',
    tone: 'neutral',
  },
  {
    key: 'accepted_by_dm',
    label: 'Accepted',
    shortLabel: 'Accepted',
    description: 'Accepted by deliveryman',
    statusQuery: 'accepted',
    tone: 'progress',
  },
  {
    key: 'preparing_in_rs',
    label: 'Processing',
    shortLabel: 'Processing',
    description: 'Packaging at the store',
    statusQuery: 'processing',
    tone: 'progress',
  },
  {
    key: 'picked_up',
    label: 'Out for Delivery',
    shortLabel: 'Out Delivery',
    description: 'Picked up and on the way',
    statusQuery: 'item_on_the_way',
    tone: 'progress',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    shortLabel: 'Delivered',
    description: 'Completed successfully',
    statusQuery: 'delivered',
    tone: 'success',
  },
];

export const ATTENTION_METRICS: AttentionMetric[] = [
  {
    key: 'searching_for_dm',
    label: 'Unassigned Orders',
    description: 'Need deliveryman',
    href: routes.orders,
    search: { status: 'searching_for_deliverymen', page: 1, search: '' },
    tone: 'warning',
  },
  {
    key: 'payment_failed',
    label: 'Payment Failed',
    description: 'Requires review',
    href: routes.orders,
    search: { status: 'failed', page: 1, search: '' },
    tone: 'error',
  },
  {
    key: 'refund_requested',
    label: 'Refund Requests',
    description: 'Requires attention',
    href: routes.refunds,
    tone: 'refund',
  },
  {
    key: 'canceled',
    label: 'Canceled',
    description: 'Orders canceled',
    href: routes.orders,
    search: { status: 'canceled', page: 1, search: '' },
    tone: 'neutral',
  },
  {
    key: 'refunded',
    label: 'Refunded',
    description: 'Completed refunds',
    href: routes.orders,
    search: { status: 'refunded', page: 1, search: '' },
    tone: 'refund',
  },
];

export function statCount(
  stats: OrderStatistics | undefined,
  key: keyof OrderStatistics,
): number | undefined {
  if (!stats) return undefined;
  const raw = stats[key];
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function formatCount(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat().format(Number(n));
}

export function formatMoney(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(n));
}

export function formatMoneyCompact(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const value = Number(n);
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (Math.abs(value) >= 10_000) {
    return `$${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return formatMoney(value);
}
